import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'

// A single checklist row. `id` is absent for not-yet-persisted (create-form) items.
export interface ChecklistRow {
  id?: string
  label: string
  is_done: boolean
}

interface Props {
  items: ChecklistRow[]
  onAdd: (label: string) => void
  onToggle: (item: ChecklistRow, next: boolean) => void
  onRemove: (item: ChecklistRow) => void
  disabled?: boolean
  variant?: 'detail' | 'form'
  /** When false, hide remove controls (e.g. edit without delete). Default true. */
  allowRemove?: boolean
}

// To-do checklist editor (feedback #69). Used on both the create form (local
// state) and the detail page (API-backed). Add an item, tick to mark done,
// remove an item. Add-and-tick is the core; rename is out of scope per feedback.
export function TaskChecklist({
  items,
  onAdd,
  onToggle,
  onRemove,
  disabled,
  variant = 'detail',
  allowRemove = true,
}: Props) {
  const { t } = useTranslation('tasks')
  const [draft, setDraft] = useState('')

  const submitDraft = () => {
    const label = draft.trim()
    if (!label) return
    onAdd(label)
    setDraft('')
  }

  if (variant === 'form') {
    return (
      <div className='space-y-3'>
        {items.map((item, idx) => (
          <div key={item.id ?? `local-${idx}`} className='space-y-1.5'>
            <label className='text-xs font-medium leading-4 text-[#171a20]'>
              {t('form.titleLabel')}
            </label>
            <div className='flex items-center gap-2'>
              <div className='flex h-10 min-w-0 flex-1 items-center gap-3 rounded-xl border border-transparent bg-[#f4f5f8] px-3'>
                <Checkbox
                  checked={item.is_done}
                  disabled={disabled}
                  className='h-4 w-4 rounded border-[#b0b5c3] data-[state=checked]:border-[#10b981] data-[state=checked]:bg-[#10b981] data-[state=checked]:text-white'
                  onCheckedChange={value => onToggle(item, value === true)}
                />
                <span
                  className={cn(
                    'truncate text-sm font-normal leading-[1.4] text-[#171a20]',
                    item.is_done && 'text-[#6c7285] line-through'
                  )}
                >
                  {item.label}
                </span>
              </div>
              {allowRemove && (
                <button
                  type='button'
                  disabled={disabled}
                  onClick={() => onRemove(item)}
                  className='inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#e7e9ef] bg-white text-[#757575] hover:bg-[#f4f5f8] disabled:opacity-50'
                  aria-label={t('checklist.remove')}
                >
                  <X className='h-4 w-4' />
                </button>
              )}
            </div>
          </div>
        ))}

        <div className='space-y-1.5'>
          <label
            htmlFor='task-checklist-draft'
            className='text-xs font-medium leading-4 text-[#171a20]'
          >
            {t('form.titleLabel')}
          </label>
          <Input
            id='task-checklist-draft'
            value={draft}
            maxLength={255}
            placeholder={t('checklist.addPlaceholder')}
            disabled={disabled}
            onChange={event => setDraft(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault()
                submitDraft()
              }
            }}
            className='h-10 rounded-xl border-transparent bg-[#f4f5f8] px-3 text-sm font-normal shadow-none'
          />
        </div>

        <Button
          type='button'
          variant='outline'
          className='h-10 rounded-xl border-[#e7e9ef] px-4 text-sm font-medium text-[#272c36]'
          disabled={disabled || !draft.trim()}
          onClick={submitDraft}
        >
          <Plus className='mr-1.5 h-4 w-4' />
          {t('checklist.add')}
        </Button>
      </div>
    )
  }

  return (
    <div>
      <div className='px-6 py-4'>
        {items.length === 0 ? (
          <p className='text-sm text-[#6c7285]'>{t('checklist.empty')}</p>
        ) : (
          <ul className='flex flex-col gap-2'>
            {items.map((item, idx) => (
              <li
                key={item.id ?? `local-${idx}`}
                className='flex h-8 items-center gap-2 border-b border-[#e7e9ef] pb-2'
              >
                <label className='flex min-w-0 flex-1 items-center gap-2'>
                  <Checkbox
                    checked={item.is_done}
                    disabled={disabled}
                    className='h-4 w-4 rounded border-[#b0b5c3] data-[state=checked]:border-[#10b981] data-[state=checked]:bg-[#10b981] data-[state=checked]:text-white'
                    onCheckedChange={v => onToggle(item, v === true)}
                  />
                  <span
                    className={cn(
                      'truncate text-sm font-medium leading-[1.4] text-[#171a20]',
                      item.is_done && 'text-[#6c7285] line-through'
                    )}
                  >
                    {item.label}
                  </span>
                </label>
                {allowRemove && (
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    className='h-5 w-5 shrink-0 rounded-none p-0 text-[#6c7285] hover:bg-transparent hover:text-[#171a20]'
                    title={t('checklist.remove')}
                    disabled={disabled}
                    onClick={() => onRemove(item)}
                  >
                    <X className='h-5 w-5' />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {!disabled && (
        <div className='flex items-start gap-4 px-6 pb-6 pt-4'>
          <Input
            value={draft}
            maxLength={255}
            placeholder={t('checklist.writePlaceholder')}
            disabled={disabled}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                submitDraft()
              }
            }}
            className='h-10 min-w-0 flex-1 rounded-xl border-transparent bg-[#f5f6fa] px-4 py-3 text-sm font-normal shadow-none'
          />
          <button
            type='button'
            className='inline-flex h-10 shrink-0 items-center gap-2 rounded-xl p-3 text-sm font-medium leading-none text-[#ff5a1f] disabled:opacity-50'
            disabled={disabled || !draft.trim()}
            onClick={submitDraft}
          >
            <Plus className='h-4 w-4' />
            {t('checklist.add')}
          </button>
        </div>
      )}
    </div>
  )
}

export default TaskChecklist
